import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { utcToZonedTime } from 'date-fns-tz';

// timeout fn sends the fetch to catch if it reaches timeout length
const timeout = function (ms, promise, controller) {
    return new Promise(function (resolve, reject) {
        setTimeout(function () {
            controller.abort();
            reject(new Error('timeout'));
        }, ms);
        promise.then(resolve, reject);
    });
};

function formatUTCDate (utcString) {
    return format(utcToZonedTime(parseISO(utcString), 'America/New_York'), 'yyyy-MM-dd');
};

function getCallLetters (content, locale = 'en') {
    const audioFeeds = content.filter(c => c.mediaState?.mediaType === 'AUDIO');
    // Prioritize HOME feed with matching language
    const homeFeed = audioFeeds.find(({ feedType, language }) => feedType === 'HOME' && language === locale);
    if (homeFeed) {
        return homeFeed.callSign;
    }
    // Fallback to any HOME feed matching the language or English
    const fallbackHomeFeed = audioFeeds.find(({ feedType, language }) => feedType === 'HOME' && (language === locale || language === 'en'));
    if (fallbackHomeFeed) {
        return fallbackHomeFeed.callSign;
    }
    // Fallback to any feed matching the language or English
    const fallbackFeed = audioFeeds.find(({ language }) => language === locale || language === 'en');
    return fallbackFeed ? fallbackFeed.callSign : null;
}

export const FGOTD_STATUS_CODE = 'login_needed_fgotd';
function parseGameMedia (data = {}, locale) {
    const { gameMedia } = data.data || {};
    if (data.errors) {
        return null;
    }
    const { contentRestriction = {}, content = {} } = gameMedia;
    const { availablePartners = [], contentRestrictionDetail = {} } = contentRestriction;

    /******************************************************
     * We only want to show the modal if we have
     * availablePartners or it's the Free Game of the Day.
    /******************************************************/
    if (availablePartners.length > 0 || contentRestrictionDetail.code === FGOTD_STATUS_CODE) {
        return {
            status: contentRestriction.contentRestrictionDetail.code,
            contentRestriction,
            callLetters: getCallLetters(content, locale)
        };
    }
    return null;
}

const localeToLanguage = {
    en: 'ENGLISH',
    es: 'SPANISH'
};

const gameMediaQuery = `
    query GameMedia($gamePk: Int!, $date: String!, $language: LanguagePreference!) {
        gameMedia(gamePk: $gamePk, date: $date) {
            contentRestriction(clientType: WEB, lang:$language){
                contentRestrictionDetail {
                    code
                    title
                    body {
                        html
                    }
                }
                availablePartners {
                    partnerTitle
                    deepLink {
                        link
                        template
                    }
                }
            }
            content {
                contentType
                feedType
                callSign
                language
                mediaState {
                    state
                    mediaType
                    contentExperience
                }
            }
        } 
    }
`;

const getWhereToWatchData = ({ gamePk, gameDate, env = 'beta', locale }) => {
    const mlbOkta = window.mlbOkta;
    const authClient = mlbOkta && mlbOkta.getAuthClient();
    const tokenManager = authClient && authClient.tokenManager.get('access_token');
    const accessToken = tokenManager && tokenManager.accessToken;

    const headers = {
        'content-type': 'application/json'
    };

    if (accessToken) {
        headers.Authorization = 'Bearer ' + accessToken;
    }

    const gameMediaEnv = (env === 'qa') ? '-qa' : '';
    const gameMediaEndpoint = 'https://media-gateway' + gameMediaEnv + '.mlb.com/graphql';
    const formattedDate = formatUTCDate(gameDate);
    const variables = {
        gamePk,
        date: formattedDate,
        language: localeToLanguage[locale] || 'ENGLISH'
    };
    const body = JSON.stringify({
        variables,
        query: gameMediaQuery
    });

    const controller = new AbortController();
    const signal = controller.signal;

    return timeout(
        10000,
        fetch(
            gameMediaEndpoint,
            {
                method: 'POST',
                headers,
                body,
                variables,
                signal
            }
        ),
        controller
    ).then(
        function (res) {
            switch (res.status) {
                case 204:
                    return {};
                case 200:
                    return res.json();
                default:
                    return {};
            }
        },
        function (err) {
            return err;
        }
    )
        .then(function (data) {
            return parseGameMedia(data, locale);
        })
        .catch(function (error) {
            console.error('error / timeout from gameMedia', error);
        });
};

export { getWhereToWatchData };
